import React, { useState, useEffect } from 'react';
import { Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography, Box, Snackbar, Alert, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText, CircularProgress } from '@mui/material';
import { Edit, Delete as DeleteIcon } from '@mui/icons-material';
import { createChecklistGroup, deleteChecklistGroup, getAllChecklistGroups, getRolesAssignedToChecklist } from 'core/API/checklist';
import styles from './TodoSetting.module.scss';
import { usePermissionCheck } from '../../../../core/hooks/usePermissionCheck';

interface ChecklistAssignmentGroup {
    id: number;
    groupName: string;
    rolesAssignedLabel: string;
    rolesAssignedId: number | null;
}

const TodoSetting = () => {
    const { hasViewPermission, hasEditPermission, hasDeletePermission, hasExecutePermission } = usePermissionCheck();
    const canViewTodo = hasViewPermission('To-Do');
    const canEditTodo = hasEditPermission('To-Do');
    const canDeleteTodo = hasDeletePermission('To-Do');
    const canExecuteTodo = hasExecutePermission('To-Do');
    const [assignmentGroups, setAssignmentGroups] = useState<ChecklistAssignmentGroup[]>([]);
    const [showAssignmentForm, setShowAssignmentForm] = useState(false);
    const [assignmentGroupName, setAssignmentGroupName] = useState('');
    const [assignmentRoles, setAssignmentRoles] = useState('');
    const [editingAssignmentId, setEditingAssignmentId] = useState<number | null>(null);
    const [dynamicRoles, setDynamicRoles] = useState<{ id: number; label: string; backgroundColor: string; textColor: string }[]>([]);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [groupToDelete, setGroupToDelete] = useState<ChecklistAssignmentGroup | null>(null);
    const [loading, setLoading] = useState(false);
    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message: string;
        severity: 'success' | 'error' | 'info' | 'warning';
    }>({
        open: false,
        message: '',
        severity: 'info',
    });

    // Fetch data on component mount
    useEffect(() => {
        if (canViewTodo) {
            fetchDynamicRoles();
            fetchTodoGroups();
        }
    }, [canViewTodo]);

    const fetchDynamicRoles = async () => {
        try {
            const res = await getRolesAssignedToChecklist();
            setDynamicRoles(Array.isArray(res) ? res : []);
        } catch (error) {
            console.error('Error fetching roles:', error);
            setDynamicRoles([]);
        }
    };

    const fetchTodoGroups = async () => {
        try {
            setLoading(true);
            const apiGroups = await getAllChecklistGroups();
            
            // Ensure we have an array
            const groupsArray = Array.isArray(apiGroups) ? apiGroups : [];
            
            const mappedGroups = groupsArray.map((g: any) => ({
                id: g.id,
                groupName: g.label || g.name || 'Unnamed Group',
                rolesAssignedLabel: g?.securityRole?.name || g?.jobCode?.label || g?.jobCode?.name || '',
                rolesAssignedId: g?.securityRole?.id || g?.jobCode?.id || null,
            }));
            
            setAssignmentGroups(mappedGroups);
        } catch (error) {
            console.error('Error fetching todo groups:', error);
            setSnackbar({
                open: true,
                message: 'Failed to fetch todo groups',
                severity: 'error',
            });
        } finally {
            setLoading(false);
        }
    };

    const handleAddAssignmentClick = () => {
        if (!canExecuteTodo) {
            return;
        }
        setShowAssignmentForm(true);
        setAssignmentGroupName('');
        setAssignmentRoles('');
        setEditingAssignmentId(null);
    };

    const handleEditAssignmentClick = (group: ChecklistAssignmentGroup) => {
        if (!canEditTodo) {
            return;
        }
        setShowAssignmentForm(true);
        setAssignmentGroupName(group.groupName);
        setAssignmentRoles(group.rolesAssignedId ? String(group.rolesAssignedId) : '');
        setEditingAssignmentId(group.id);
    };

    const handleSaveAssignment = async () => {
        if (!canEditTodo && !canExecuteTodo) {
            return;
        }
        if (!assignmentGroupName.trim() || !assignmentRoles) {
            setSnackbar({
                open: true,
                message: 'Please fill in both Group Name and Roles Assigned',
                severity: 'warning',
            });
            return;
        }

        try {
            const workplaceId = Number(localStorage.getItem('workplace-id') || '1');
            const userId = Number(localStorage.getItem('userId') || '1');
            const payload = {
                label: assignmentGroupName,
                securityRole: assignmentRoles ? `/api/security_roles/${assignmentRoles}` : null,
                author: `/api/users/${userId}`,
                workplace: `/api/workplaces/${workplaceId}`,
                isUpdate: editingAssignmentId
            };

            await createChecklistGroup(payload);

            setSnackbar({
                open: true,
                message: editingAssignmentId ? 'Todo group updated successfully' : 'Todo group created successfully',
                severity: 'success',
            });

            // Refresh the groups list
            await fetchTodoGroups();

            // Reset form
            setShowAssignmentForm(false);
            setAssignmentGroupName('');
            setAssignmentRoles('');
            setEditingAssignmentId(null);
        } catch (error) {
            console.error('Error saving todo group:', error);
            setSnackbar({
                open: true,
                message: 'Failed to save todo group',
                severity: 'error',
            });
        }
    };

    const handleDeleteAssignmentClick = async (id: number) => {
        if (!canDeleteTodo) {
            return;
        }
        const group = assignmentGroups.find(g => g.id === id);
        if (group) {
            setGroupToDelete(group);
            setDeleteDialogOpen(true);
        }
    };

    const handleDeleteConfirm = async () => {
        if (!canDeleteTodo) {
            return;
        }
        if (!groupToDelete) return;
        
        try {
            await deleteChecklistGroup(groupToDelete.id);
            setAssignmentGroups(assignmentGroups.filter((group) => group.id !== groupToDelete.id));
            setSnackbar({
                open: true,
                message: 'Todo group deleted successfully',
                severity: 'success',
            });
        } catch (error) {
            console.error('Error deleting todo group:', error);
            
            // Extract backend error message
            let errorMessage = 'Failed to delete todo group';
            if (error && typeof error === 'object') {
                // Since axios interceptor returns error.response, the error is actually the response
                if ('data' in error && error.data && typeof error.data === 'object' && 'error' in error.data) {
                    errorMessage = (error.data as any).error;
                } else if ('statusText' in error) {
                    errorMessage = (error as any).statusText || `HTTP ${(error as any).status}`;
                } else if ('message' in error) {
                    errorMessage = (error as Error).message;
                }
            }
            
            setSnackbar({
                open: true,
                message: errorMessage,
                severity: 'error',
            });
        } finally {
            setDeleteDialogOpen(false);
            setGroupToDelete(null);
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setGroupToDelete(null);
    };

    const handleCancelAssignment = () => {
        setShowAssignmentForm(false);
        setAssignmentGroupName('');
        setAssignmentRoles('');
        setEditingAssignmentId(null);
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    if (!canViewTodo) {
        return (
            <Paper elevation={3} className={styles.containerr}>
                <div className={styles.header}>
                    <Typography variant="h6">Checklist and To-Do Assignment Groups</Typography>
                </div>
                <div style={{ padding: '20px', textAlign: 'center' }}>
                    You do not have permission to view todo settings.
                </div>
            </Paper>
        );
    }

    return (
        <Paper elevation={3} className={styles.containerr}>
            <div className={styles.header}>
                <Typography variant="h6">Checklist and To-Do Assignment Groups</Typography>
                <Button
                    onClick={handleAddAssignmentClick}
                    style={{
                        backgroundColor: canExecuteTodo ? 'rgb(68, 99, 136)' : 'rgba(68, 99, 136, 0.6)',
                        color: 'white',
                        padding: '5px 15px',
                        borderRadius: '4px',
                        fontSize: '14px',
                        cursor: canExecuteTodo ? 'pointer' : 'not-allowed',
                        textTransform: 'capitalize',
                    }}
                    disabled={!canExecuteTodo}
                    title={canExecuteTodo ? 'Add' : 'You do not have permission to add'}
                >
                    Add
                </Button>
            </div>
            {showAssignmentForm && (
                <Box className={styles.formContainer}>
                    <TextField
                        label="Group Name"
                        value={assignmentGroupName}
                        onChange={(e) => setAssignmentGroupName(e.target.value)}
                        size="small"
                        fullWidth
                        disabled={!canExecuteTodo && !canEditTodo}
                    />
                    <TextField
                        select
                        label="Roles Assigned"
                        value={assignmentRoles}
                        onChange={(e) => setAssignmentRoles(e.target.value)}
                        size="small"
                        fullWidth
                        className={styles.dropdown}
                        disabled={!canExecuteTodo && !canEditTodo}
                    >
                        <MenuItem value="">-- Select Role --</MenuItem>
                        {Array.isArray(dynamicRoles) && dynamicRoles.length > 0 ? (
                            dynamicRoles.map((role) => (
                                <MenuItem key={role.id} value={role.id}>
                                    {role.label}
                                </MenuItem>
                            ))
                        ) : (
                            <MenuItem value="">No roles available</MenuItem>
                        )}
                    </TextField>
                    <Button
                        variant="contained"
                        className={styles.saveButton}
                        onClick={handleSaveAssignment}
                        disabled={!assignmentGroupName.trim() || !assignmentRoles || (!canExecuteTodo && !canEditTodo)}
                        title={
                            !canExecuteTodo && !canEditTodo
                                ? 'You do not have permission to save'
                                : undefined
                        }
                    >
                        {editingAssignmentId ? 'Update' : 'Save'}
                    </Button>
                    <Button
                        variant="outlined"
                        className={styles.cancelButton}
                        onClick={handleCancelAssignment}
                    >
                        Cancel
                    </Button>
                </Box>
            )}
            <div className={styles.tableContainer}>
                <table className={styles.workplaceTable}>
                    <thead>
                        <tr>
                            <th>Group Name</th>
                            <th>Roles Assigned</th>
                            <th style={{ padding: '8px', width: '48px', textAlign: 'center' }}>Edit</th>
                            <th style={{ padding: '8px', width: '48px', textAlign: 'center' }}>Delete</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan={4} style={{ textAlign: 'center', padding: '40px' }}>
                                    <CircularProgress size={20} color="inherit" />
                                </td>
                            </tr>
                        ) : assignmentGroups.length > 0 ? (
                            assignmentGroups.map((group) => (
                                <tr key={group.id}>
                                    <td>{group.groupName}</td>
                                    <td>{group.rolesAssignedLabel || 'No role assigned'}</td>
                                    <td style={{ textAlign: 'center', padding: '8px', width: 48 }}>
                                        <button
                                            className={styles.iconButton}
                                            onClick={() => handleEditAssignmentClick(group)}
                                            title={canEditTodo ? 'Edit' : 'You do not have permission to edit'}
                                            disabled={!canEditTodo}
                                            style={{ opacity: canEditTodo ? 1 : 0.5, cursor: canEditTodo ? 'pointer' : 'not-allowed' }}
                                        >
                                            <Edit className={styles.editIcon} />
                                        </button>
                                    </td>
                                    <td style={{ textAlign: 'center', padding: '8px', width: 48 }}>
                                        <button
                                            className={styles.iconButton}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                if (canDeleteTodo) {
                                                    handleDeleteAssignmentClick(group.id);
                                                }
                                            }}
                                            title={canDeleteTodo ? 'Delete' : 'You do not have permission to delete'}
                                            disabled={!canDeleteTodo}
                                            style={{ opacity: canDeleteTodo ? 1 : 0.5, cursor: canDeleteTodo ? 'pointer' : 'not-allowed' }}
                                        >
                                            <DeleteIcon className={styles.deleteIcon} />
                                        </button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={4} style={{ textAlign: 'center', padding: '20px' }}>
                                    No records found
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert 
                    onClose={handleCloseSnackbar} 
                    severity={snackbar.severity}
                    variant="filled"
                    className={styles.snackbar}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={handleDeleteCancel}
                aria-labelledby="delete-dialog-title"
                aria-describedby="delete-dialog-description"
                maxWidth="sm"
                fullWidth
                disableEscapeKeyDown={false}
                style={{ zIndex: 9999 }}
                PaperProps={{
                    style: {
                        backgroundColor: 'white',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                        borderRadius: '8px',
                        minWidth: '400px'
                    }
                }}
            >
                <DialogTitle id="delete-dialog-title">
                    Confirm Delete
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="delete-dialog-description">
                        Are you sure you want to delete the todo group &ldquo;{groupToDelete?.groupName}&rdquo;? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteCancel} style={{ color: 'black' }}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleDeleteConfirm} 
                        style={{ backgroundColor: 'red', color: 'white' }}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export default TodoSetting;